import React, { useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';

// ─── Engine badge config ──────────────────────────────────────────────────────
const ENGINE_CONFIG = {
  lead:      { label: 'LE',   color: '#3b82f6', bg: 'rgba(59,130,246,0.12)',  title: 'Lead Engine' },
  t2m:       { label: 'T2M',  color: '#eab308', bg: 'rgba(234,179,8,0.12)',   title: 'Trial to Member' },
  pb101:     { label: 'PB101',color: '#a855f7', bg: 'rgba(168,85,247,0.12)',  title: 'PB101' },
  // Added 9/25/26 — retention_actions rows now flow into this same timeline
  // (see member-timeline route, index.js) instead of silently falling back
  // to the Lead Engine badge via the ENGINE_CONFIG[entry.module] || .lead
  // default below.
  retention: { label: 'RET',  color: '#22c55e', bg: 'rgba(34,197,94,0.12)',   title: 'Retention' },
  // Salesmsg texts/calls (10/7/26): shown as an expandable badge on the day's logged outcome, or
  // as its own compact row on days with no logged outcome.
  membership_switch: { label: 'SW', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', title: 'Membership at another club' },
  events_sponsorship: { label: 'ES', color: '#ec4899', bg: 'rgba(236,72,153,0.12)', title: 'Events & Sponsorship' },
  salesmsg:  { label: 'MSG',  color: '#14b8a6', bg: 'rgba(20,184,166,0.12)',  title: 'Salesmsg' },
};

const CHICAGO_DAY = { timeZone: 'America/Chicago' };
function chicagoDay(ts) {
  return new Date(ts).toLocaleDateString('en-CA', CHICAGO_DAY); // YYYY-MM-DD
}
function fmtClock(ts) {
  return new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}
function fmtDuration(secs) {
  if (!secs) return '0:00';
  return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
}

// One day of Salesmsg activity. Collapsed it is a single small chip so a long thread never pushes the
// timeline around; expanded it shows the conversation in a scrollable box.
function SalesmsgBadge({ day, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const msgs = day.messages || [];
  const textsOut = msgs.filter(m => m.kind === 'text' && m.direction === 'out' && !m.automated).length;
  const textsAuto = msgs.filter(m => m.kind === 'text' && m.direction === 'out' && m.automated).length;
  const textsIn = msgs.filter(m => m.kind === 'text' && m.direction === 'in').length;
  const calls = msgs.filter(m => m.kind === 'call');
  const bits = [];
  if (textsOut) bits.push(`${textsOut} text${textsOut > 1 ? 's' : ''} sent`);
  if (textsIn) bits.push(`${textsIn} repl${textsIn > 1 ? 'ies' : 'y'}`);
  if (textsAuto) bits.push(`${textsAuto} auto`);
  for (const c of calls) {
    const label = c.direction === 'out' ? 'call' : 'inbound call';
    bits.push(c.call_outcome === 'answered' ? `${label} ${fmtDuration(c.duration_secs)}` : `${label} ${c.call_outcome || 'no answer'}`);
  }
  const teal = ENGINE_CONFIG.salesmsg.color;
  return (
    <div style={{ marginTop: 6 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 8px',
          background: `${teal}12`, border: `1px solid ${teal}40`, borderRadius: 4,
          fontSize: 11, color: teal, cursor: 'pointer', maxWidth: '100%', textAlign: 'left',
        }}
        title="Salesmsg activity that day"
      >
        <span>💬</span>
        <span style={{ fontWeight: 600 }}>{bits.join(' · ') || 'Salesmsg activity'}</span>
        <span style={{ fontSize: 9 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{
          marginTop: 6, maxHeight: 260, overflowY: 'auto', padding: '8px 10px',
          background: 'var(--surface-3)', borderRadius: 6, borderLeft: `2px solid ${teal}60`,
        }}>
          {msgs.map((m, i) => {
            const mine = m.direction === 'out';
            return (
              <div key={i} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 6 }}>
                <div style={{
                  maxWidth: '85%', padding: '5px 9px', borderRadius: 8, fontSize: 12, lineHeight: 1.45,
                  background: mine ? `${teal}22` : 'var(--surface-2, rgba(255,255,255,0.06))',
                  border: `1px solid ${mine ? `${teal}40` : 'var(--border)'}`,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>
                  {m.kind === 'call'
                    ? <span>📞 {mine ? 'Outbound' : 'Inbound'} call — {m.call_outcome === 'answered' ? `answered, ${fmtDuration(m.duration_secs)}` : (m.call_outcome || 'no answer')}</span>
                    : (m.body || <em style={{ color: 'var(--text-secondary)' }}>(no text)</em>)}
                  <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 2 }}>
                    {mine ? (m.who || 'Staff') : 'Member'}{m.automated ? ' · auto' : ''} · {fmtClock(m.at)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Outcome category colors ──────────────────────────────────────────────────
function outcomeColor(outcome) {
  if (!outcome) return 'var(--text-secondary)';
  const o = outcome.toLowerCase();
  if (o.includes('converted') || o.includes('sold'))   return '#22c55e';
  if (o.includes('hard no')   || o.includes('lost') || o.includes('declined')) return '#ef4444';
  if (o.includes('soft no'))  return '#f97316';
  if (o.includes('interested') || o.includes('on track')) return '#3b82f6';
  if (o.includes('at risk'))  return '#ef4444';
  if (o.includes('no answer') || o.includes('voicemail')) return '#6b7280';
  if (o.includes('re-engage') || o.includes('re engage')) return '#8b5cf6';
  return 'var(--text)';
}

// ─── Format timestamp ─────────────────────────────────────────────────────────
function formatTimestamp(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${date} · ${time}`;
}

function formatFollowUpDate(dateStr, timeStr) {
  if (!dateStr) return null;
  // Parse date parts explicitly to avoid UTC timezone shift
  const parts = String(dateStr).split('T')[0].split('-');
  if (parts.length < 3) return null;
  const year = parseInt(parts[0]), month = parseInt(parts[1]) - 1, day = parseInt(parts[2]);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const fuDate = new Date(year, month, day);
  const diffDays = Math.round((fuDate - today) / 86400000);

  let label;
  if (timeStr) {
    const timeParts = String(timeStr).split(':');
    const h = parseInt(timeParts[0]) || 0, m = parseInt(timeParts[1]) || 0;
    const d = new Date(year, month, day, h, m);
    label = d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
  } else {
    label = fuDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  let urgencyColor = '#3b82f6';
  let urgencyLabel = '';
  if (diffDays < 0)     { urgencyColor = '#ef4444'; urgencyLabel = 'OVERDUE'; }
  else if (diffDays === 0) { urgencyColor = '#ef4444'; urgencyLabel = 'TODAY'; }
  else if (diffDays === 1) { urgencyColor = '#f97316'; urgencyLabel = 'TOMORROW'; }

  return { label, urgencyColor, urgencyLabel };
}

// ─── Single timeline entry ────────────────────────────────────────────────────
function TimelineEntry({ entry, isLast }) {
  const engine = ENGINE_CONFIG[entry.module] || ENGINE_CONFIG.lead;
  const isMsgOnly = entry.module === 'salesmsg';
  const fuInfo = formatFollowUpDate(entry.follow_up_date, entry.follow_up_time);

  return (
    <div style={{ display: 'flex', gap: 12, position: 'relative' }}>

      {/* Spine line */}
      {!isLast && (
        <div style={{
          position: 'absolute', left: 17, top: 32, bottom: -8,
          width: 1, background: 'var(--border)',
        }} />
      )}

      {/* Engine dot */}
      <div style={{ flexShrink: 0, paddingTop: 4 }}>
        <div style={{
          width: 34, height: 22,
          background: engine.bg,
          border: `1px solid ${engine.color}40`,
          borderRadius: 4,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 9, fontWeight: 800, color: engine.color,
          letterSpacing: '0.04em',
        }}
          title={engine.title}
        >
          {engine.label}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, paddingBottom: isLast ? 0 : 16 }}>

        {/* Top row: outcome + timestamp */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <span style={{
            fontSize: 13, fontWeight: 600,
            color: outcomeColor(entry.outcome),
          }}>
            {isMsgOnly ? 'Salesmsg activity' : (entry.outcome || 'Contact Logged')}
          </span>
          <span style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap', flexShrink: 0 }}>
            {formatTimestamp(entry.logged_at)}
          </span>
        </div>

        {/* Rep + contact method */}
        {!isMsgOnly && (
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
          {entry.logged_by_name || 'Staff'}
          {entry.contact_method && (
            <span style={{ marginLeft: 6, color: '#555' }}>· via {entry.contact_method}</span>
          )}
        </div>
        )}

        {/* Notes */}
        {entry.notes && entry.notes.trim() && (
          <div style={{
            fontSize: 12, color: 'var(--text)',
            marginTop: 5, lineHeight: 1.55,
            padding: '6px 9px',
            background: 'var(--surface-3)',
            borderRadius: 5,
            borderLeft: `2px solid ${engine.color}50`,
          }}>
            {entry.notes}
          </div>
        )}

        {/* Close reason / Why field */}
        {entry.close_reason && (
          <div style={{
            fontSize: 11, color: 'var(--text-secondary)',
            fontStyle: 'italic', marginTop: 4,
          }}>
            Why: {entry.close_reason}
          </div>
        )}

        {/* Salesmsg thread for this day: complementary to the logged outcome above */}
        {entry.salesmsg && <SalesmsgBadge day={entry.salesmsg} />}

        {/* Follow-up pill */}
        {fuInfo && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            marginTop: 6,
            padding: '3px 8px',
            background: `${fuInfo.urgencyColor}12`,
            border: `1px solid ${fuInfo.urgencyColor}30`,
            borderRadius: 4,
            fontSize: 11,
          }}>
            <span style={{ color: fuInfo.urgencyColor }}>📅</span>
            <span style={{ color: fuInfo.urgencyColor, fontWeight: fuInfo.urgencyLabel ? 700 : 400 }}>
              {fuInfo.urgencyLabel ? `${fuInfo.urgencyLabel} · ` : ''}{fuInfo.label}
            </span>
            {entry.follow_up_note && (
              <span style={{ color: 'var(--text-secondary)', marginLeft: 2 }}>
                — {entry.follow_up_note}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
// Props:
//   memberId  — required, the member_id to load history for
//   memberName — optional, displayed in empty state
//   maxHeight  — optional, enables scroll (default: none)
//   compact    — optional, hides header (for embedding inside T2M detail)
//   saleId     — optional, a commission sale: also pulls in the records linked to that sale
//                (its PlayByPoint record often has no email, so the email match misses it)

export default function MemberTimeline({ memberId, memberName, maxHeight, compact = false, saleId }) {
  const { get } = useApi();
  const [entries, setEntries] = useState([]);
  const [msgDays, setMsgDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [filter, setFilter]   = useState('all'); // all | lead | t2m | pb101

  useEffect(() => {
    if (!memberId) return;
    setLoading(true);
    setError(null);
    get(`/member/timeline/${encodeURIComponent(memberId)}${saleId ? `?saleId=${encodeURIComponent(saleId)}` : ''}`)
      .then(d => { setEntries(d?.entries || []); setMsgDays(d?.salesmsgDays || []); })
      .catch(e => setError(e.message || 'Failed to load timeline'))
      .finally(() => setLoading(false));
  }, [memberId, saleId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Attach each day's Salesmsg thread to that day's most recent logged outcome; days with no logged
  // outcome get their own compact row. Logged outcomes stay the record; the thread is the evidence.
  const merged = React.useMemo(() => {
    const out = entries.map(e => ({ ...e }));
    const leftovers = [];
    for (const day of msgDays) {
      const target = out.find(e => chicagoDay(e.logged_at) === day.day);
      if (target) target.salesmsg = day;
      else leftovers.push({ log_id: `sm-${day.day}`, module: 'salesmsg', logged_at: day.logged_at, salesmsg: day });
    }
    return [...out, ...leftovers].sort((a, b) => new Date(b.logged_at) - new Date(a.logged_at));
  }, [entries, msgDays]);

  const filtered = filter === 'all'
    ? merged
    : merged.filter(e => e.module === filter);

  // Count per module for filter pills
  const counts = merged.reduce((acc, e) => {
    acc[e.module] = (acc[e.module] || 0) + 1;
    return acc;
  }, {});

  return (
    <div>
      {/* Header */}
      {!compact && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 12,
        }}>
          <div className="section-header" style={{ marginBottom: 0 }}>
            Member Timeline
            {entries.length > 0 && (
              <span style={{
                marginLeft: 8, fontSize: 11, fontWeight: 600,
                color: 'var(--text-secondary)', fontFamily: 'monospace',
              }}>
                {entries.length}
              </span>
            )}
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
            Read-only · Audit trail
          </div>
        </div>
      )}

      {compact && entries.length > 0 && (
        <div style={{
          fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)',
          textTransform: 'uppercase', letterSpacing: '0.06em',
          marginBottom: 10,
        }}>
          Member Timeline · {entries.length}
        </div>
      )}

      {/* Module filter pills — only show if entries from multiple modules */}
      {entries.length > 0 && Object.keys(counts).length > 1 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          {[
            { key: 'all', label: 'All', count: merged.length, color: '#888' },
            counts.lead  && { key: 'lead',  ...ENGINE_CONFIG.lead,  count: counts.lead },
            counts.t2m   && { key: 't2m',   ...ENGINE_CONFIG.t2m,   count: counts.t2m },
            counts.pb101 && { key: 'pb101', ...ENGINE_CONFIG.pb101, count: counts.pb101 },
            counts.salesmsg && { key: 'salesmsg', ...ENGINE_CONFIG.salesmsg, count: counts.salesmsg },
          ].filter(Boolean).map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              style={{
                padding: '3px 10px', borderRadius: 4,
                fontSize: 11, fontWeight: 600, cursor: 'pointer',
                border: `1px solid ${filter === f.key ? f.color : 'var(--border)'}`,
                background: filter === f.key ? `${f.color}15` : 'transparent',
                color: filter === f.key ? f.color : 'var(--text-secondary)',
                transition: 'all 0.1s',
              }}
            >
              {f.key === 'all' ? 'All' : f.label} {f.count}
            </button>
          ))}
        </div>
      )}

      {/* Scrollable list */}
      <div style={{
        maxHeight: maxHeight || undefined,
        overflowY: maxHeight ? 'auto' : undefined,
        paddingRight: maxHeight ? 4 : undefined,
      }}>
        {loading && (
          <div style={{ padding: '16px 0', color: 'var(--text-secondary)', fontSize: 13 }}>
            Loading timeline…
          </div>
        )}

        {error && (
          <div style={{
            padding: '10px 12px', borderRadius: 6,
            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
            fontSize: 12, color: '#ef4444',
          }}>
            {error}
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div style={{
            padding: '20px 0', color: 'var(--text-secondary)',
            fontSize: 13, textAlign: 'center',
          }}>
            {entries.length === 0
              ? `No contact history yet${memberName ? ` for ${memberName.split(' ')[0]}` : ''}.`
              : 'No entries for this filter.'}
          </div>
        )}

        {!loading && !error && filtered.map((entry, i) => (
          <TimelineEntry
            key={entry.log_id || i}
            entry={entry}
            isLast={i === filtered.length - 1}
          />
        ))}
      </div>
    </div>
  );
}
